import { format } from 'date-fns';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { useMutation } from '@tanstack/react-query';

import type {
  ReceiptBlockState,
  ReceiptScanMachine,
  ScanFieldKey,
  ScanFormSnapshot,
  ScanMachineCallbacks,
} from '@splitfiesta/core';
import { createReceiptScanMachine } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type {
  ReceiptDraftContentType,
  ReceiptDraftResponseDto,
  ReceiptScanResultDto,
  ScanReceiptDraftDto,
  SuccessResponseDto,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';
import { useScanSession } from '@/stores/scanSessionStore';

import { toScanJpeg } from '@/utils/receiptImage';
import { toast } from '@/utils/toast';

/** A stalled PUT with no TCP error would otherwise hold "Uploading…" until Remove. */
export const RECEIPT_PUT_TIMEOUT_MS = 60_000;

function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export interface UseReceiptScan {
  state: ReceiptBlockState;
  machine: ReceiptScanMachine;
  locked: boolean;
  scanInFlight: boolean;
  draftId: string | null;
  tags: ReadonlySet<ScanFieldKey>;
  submitLabel: 'reading' | 'uploading' | null;
}

export interface UseReceiptScanArgs {
  groupId: string | null;
  groupCurrency: string;
  locale: string;
  mode: 'create' | 'edit';
  /** Read once, when the machine is created. */
  savedReceipt: string | null;
  /** Read once, when the machine is created. */
  collapsed: boolean;
  snapshot: () => ScanFormSnapshot;
  onFill: ScanMachineCallbacks['onFill'];
  onLines: ScanMachineCallbacks['onLines'];
}

/**
 * The receipt block's state on web: the core scan machine with the browser
 * adapters (canvas JPEG, the draft and scan endpoints, a `fetch` PUT).
 */
export function useReceiptScan(args: UseReceiptScanArgs): UseReceiptScan {
  const api = useTypedApi();
  const { t } = useTranslation();
  const updateUser = useAuthStore(state => state.updateUser);

  // The machine outlives renders, so its callbacks read the latest values.
  const latest = useRef({ args, api, t, updateUser });
  useEffect(() => {
    latest.current = { args, api, t, updateUser };
  });

  // The machine gates on connectivity itself, and a draft-cap 429 or a
  // ProNotAvailable is final: React Query must neither pause nor retry.
  const createDraftMutation = useMutation({
    mutationFn: async (input: {
      groupId: string;
      contentLength: number;
      contentType: ReceiptDraftContentType;
    }) => {
      const response = (await latest.current.api.receipts.createDraft(
        input.groupId,
        { contentLength: input.contentLength, contentType: input.contentType }
      )) as SuccessResponseDto<ReceiptDraftResponseDto>;
      return response.data;
    },
    retry: false,
    networkMode: 'always',
  });
  const scanMutation = useMutation({
    mutationFn: async (input: {
      groupId: string;
      draftId: string;
      body: ScanReceiptDraftDto;
    }) => {
      const response = (await latest.current.api.receipts.scanDraft(
        input.groupId,
        input.draftId,
        input.body
      )) as SuccessResponseDto<ReceiptScanResultDto>;
      return response.data;
    },
    retry: false,
    networkMode: 'always',
  });
  const mutations = useRef({
    createDraft: createDraftMutation.mutateAsync,
    scan: scanMutation.mutateAsync,
  });
  useEffect(() => {
    mutations.current = {
      createDraft: createDraftMutation.mutateAsync,
      scan: scanMutation.mutateAsync,
    };
  });

  const objectUrls = useRef<string[]>([]);

  // The refs are read only inside the adapters and callbacks, which the
  // machine calls after render, never while creating it.
  // eslint-disable-next-line react-hooks/refs
  const [machine] = useState(() =>
    createReceiptScanMachine(
      {
        encode: async picked => {
          const file = picked as File;
          if (isPdfFile(file)) {
            const localUri = URL.createObjectURL(file);
            objectUrls.current.push(localUri);
            return {
              localUri,
              bytes: file.size,
              body: file,
              contentType: 'application/pdf',
              fileName: file.name,
            };
          }
          const { blob } = await toScanJpeg(file);
          const localUri = URL.createObjectURL(blob);
          objectUrls.current.push(localUri);
          return {
            localUri,
            bytes: blob.size,
            body: blob,
            contentType: 'image/jpeg',
          };
        },
        createDraft: (groupId, contentLength, contentType) =>
          mutations.current.createDraft({
            groupId,
            contentLength,
            contentType,
          }),
        put: async (upload, body, signal) => {
          const deadline = new AbortController();
          const abort = () => deadline.abort();
          signal.addEventListener('abort', abort);
          const timer = setTimeout(abort, RECEIPT_PUT_TIMEOUT_MS);
          try {
            // Only the signed headers: the browser derives Content-Length from
            // the Blob and refuses to let script set it.
            const response = await fetch(upload.url, {
              method: 'PUT',
              headers: upload.headers,
              body: body as Blob,
              signal: deadline.signal,
            });
            if (!response.ok) {
              throw new Error(`Receipt upload failed: ${response.status}`);
            }
          } finally {
            clearTimeout(timer);
            signal.removeEventListener('abort', abort);
          }
        },
        scan: (groupId, draftId, body) =>
          mutations.current.scan({ groupId, draftId, body }),
        now: () => Date.now(),
      },
      {
        snapshot: () => latest.current.args.snapshot(),
        groupCurrency: () => latest.current.args.groupCurrency,
        locale: () => latest.current.args.locale,
        localDate: () => format(new Date(), 'yyyy-MM-dd'),
        onFill: (fills, tags) => latest.current.args.onFill(fills, tags),
        onLines: (lines, reconciled) =>
          latest.current.args.onLines(lines, reconciled),
        onQuota: (quota, fairUse) => {
          if (fairUse) useScanSession.getState().markFairUseHit();
          // A Pro fair-use refusal carries no quota: the stored one stands.
          if (quota) latest.current.updateUser({ scanQuota: quota });
        },
        onProNotAvailable: () =>
          useScanSession.getState().markProNotAvailable(),
        onUploadFailed: () => {
          toast.warning(
            latest.current.t('expenses:errors.receiptUploadFailed')
          );
        },
        onDraftExpired: () => {
          toast.error(latest.current.t('errors:scan.draftNotFound'));
        },
        // Web has no haptics; the row's live region announces the result.
        onSuccessFeedback: () => undefined,
      },
      { savedUrl: args.savedReceipt, collapsed: args.collapsed }
    )
  );

  const state = useSyncExternalStore(machine.subscribe, machine.getState);

  useEffect(
    () => () => {
      machine.abort();
      for (const url of objectUrls.current) URL.revokeObjectURL(url);
      objectUrls.current = [];
    },
    [machine]
  );

  // `tags()` returns a fresh Set each call; a stable one keeps callers'
  // memos and effects quiet until a tag actually changes.
  const tagKey = [...machine.tags()].sort().join(',');
  const tags = useMemo(
    () =>
      new Set(
        tagKey ? (tagKey.split(',') as ScanFieldKey[]) : []
      ) as ReadonlySet<ScanFieldKey>,
    [tagKey]
  );

  const draft = state.kind === 'saved' ? state.replacing : state;
  const locked = machine.locked();
  const submitLabel = locked
    ? 'reading'
    : draft?.kind === 'uploading'
      ? 'uploading'
      : null;

  return {
    state,
    machine,
    locked,
    scanInFlight: machine.scanInFlight(),
    draftId: machine.draftId(),
    tags,
    submitLabel,
  };
}
